import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Injector, input, output, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Button2Component } from '@frontendlib/components/components/button/button2.component';
import { IconComponent } from '@frontendlib/components/components/icon/icon.component';
import { PinnedIconComponent } from '@frontendlib/components/components/icon/pinned-icon.component';
import { SearchLocationBadgeComponent } from '@frontendlib/components/components/search-location-badge/search-location-badge.component';
import { DateFormatPipe } from '@modules/common/pipes/date-format.pipe';
import { htmlToPlainText } from '@modules/common/utils/html';
import { DragNDropModule } from '@modules/drag-n-drop/drag-n-drop.module';
import { SelectableService } from '@modules/drag-n-drop/services/selectable.service';
import { SpaceContextStore } from '@modules/common/services/SpaceContextStore';
import { type LinkedInfo } from '@modules/linked-info/types/linked-info';
import { type DragData } from '@modules/drag-n-drop/types/drag-data';
import { FlaggedIconComponent } from '@modules/elements/components/indicator-icons/flagged-icon.component';
import { LinkedInfoListComponent } from '@modules/elements/components/link-info/linked-info-list/linked-info-list.component';
import { TrashActionComponent } from '@modules/elements/components/stitch-trash-action/stitch-trash-action.component';
import { StitchInfoPopoverDirective } from '@modules/elements/directives/stitch-info-popover.directive';
import { QuickActionComponent } from '@modules/form-controls/components/quick-action/quick-action.component';
import { LinkedInfoService } from '@modules/linked-info/services/linked-info.service';
import { StitchItemOpenerService } from '@modules/linked-info/services/stitch-item-opener.service';
import { type ListState as StitchListState } from '@modules/linked-info/types/list-state';
import { UtilsModule } from '@modules/utils/utils.module';
import type { StitchItemSummaryDto } from '@sharedlib/dtos/dto/StitchItemSummaryDto';
import { combineLatest, map, of, switchMap, tap } from 'rxjs';
import { Labels } from '../../../../../i18n/labels';
import { BoardActionsService } from '../../../services/board-actions.service';
import { BoardsStore } from '../../../services/boards.store';
import { type Board } from '../../../types/board';
import { toBoardDragData } from '../../shared/board-drag-data.util';
import { canDropOnBoard, dropOnBoard } from '../../../drag/board-stitch-drop';
import { BoardThumbnailComponent } from '../../shared/board-thumbnail.component';
import { thumbnailElements } from '../../shared/board-thumbnail.util';
import { toBoardStitchSummary } from '../../shared/board-stitch-summary.util';
import { BoardContextMenuDirective } from '../../menus/board-context-menu.directive';

const STITCH_KEYS = ['id', 'itemType'] as const;

/** Whether two summaries would fetch the same linked info: same item, same `hasStitched*` flags. */
function sameStitchKey(a: StitchItemSummaryDto, b: StitchItemSummaryDto): boolean {
  if (STITCH_KEYS.some((key) => a[key] !== b[key])) return false;
  const flags = (summary: StitchItemSummaryDto): Array<[string, unknown]> =>
    Object.entries(summary).filter(([key]) => key.startsWith('hasStitched'));
  const left = flags(a);
  const right = new Map(flags(b));
  return left.length === right.size && left.every(([key, value]) => right.get(key) === value);
}

function isBoard(value: unknown): value is Board {
  return typeof value === 'object' && value !== null && 'id' in value && 'headVersion' in value;
}

@Component({
  selector: 'app-board-card',
  templateUrl: './board-card.component.html',
  styleUrls: [
    '../../../../common/components/base-stitch/base-stitch.component.less',
    '../../../../elements/components/note/note.component.less',
    './board-card.component.less',
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    BoardThumbnailComponent,
    Button2Component,
    IconComponent,
    MatTooltipModule,
    DateFormatPipe,
    FlaggedIconComponent,
    PinnedIconComponent,
    TrashActionComponent,
    QuickActionComponent,
    UtilsModule,
    BoardContextMenuDirective,
    DragNDropModule,
    SearchLocationBadgeComponent,
    StitchInfoPopoverDirective,
    LinkedInfoListComponent,
  ],
})
export class BoardCardComponent {
  public readonly board = input.required<Board>();
  public readonly dragEnabled = input(true);
  protected readonly isDragging = signal(false);
  /** This card's index in its host list — feeds `SelectableService` for multi-select and drag data, the
   * same convention `BaseStitchComponent`'s cards use elsewhere. */
  public readonly position = input<number>();
  /** Shows which folder the board belongs to — same "location badge" Notes/Files show only while a search
   * is narrowing their own list, where same-named items from different folders would otherwise look
   * identical (SBUX-988 review). A folder-scoped list never sets this: every card there already belongs
   * to the one folder being browsed. */
  public readonly showLocationBadge = input(false);
  public readonly open = output<void>();

  protected readonly store = inject(BoardsStore);
  private readonly actions = inject(BoardActionsService);
  private readonly selectableService = inject(SelectableService, { optional: true });
  private readonly linkedInfoService = inject(LinkedInfoService);
  private readonly stitchItemOpener = inject(StitchItemOpenerService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly Labels = Labels;
  protected readonly contextMenuOpened = signal(false);
  protected readonly stitchExpanded = signal(false);
  protected readonly popoverListState: StitchListState = {
    sort: { by: 'title', order: 'asc' },
    filters: { stitch: {} },
    view: 'short',
  };

  /** Same popover/icon this card's bottom bar didn't have before — mirrored from `stch-bottom-bar` (used
   * by the mail item and every other stitch list item) rather than reused directly, because this card's
   * bottom bar hand-rolls its own snoozed/followUp/flag/pin icons with board-specific tooltip text, which
   * don't map onto that component's single-date `StitchItemSummaryDto` shape.
   *
   * Equal while the board's id and its `hasStitched*` flags are: those are all `trackLinkedInfo` keys its
   * fetch on, so a pin, a flag or an autosave (each a new board object) neither re-emits nor refetches. The
   * `hasStitched*` short-circuit applies, like every other list row: a board with nothing stitched costs no
   * request, and a STITCHED/UNSTITCHED signal for it still forces one. */
  protected readonly stitchSummary = computed<StitchItemSummaryDto>(() => toBoardStitchSummary(this.board()), {
    equal: sameStitchKey,
  });

  private linkedItems: readonly LinkedInfo[] = [];

  protected readonly canDropStitch = (data: DragData): boolean =>
    this.dragEnabled() &&
    canDropOnBoard(data, { target: this.board(), spaceId: this.injector.get(SpaceContextStore).contextId() ?? '', linkedItems: this.linkedItems });

  protected dropStitch(data: DragData): void {
    if (!this.dragEnabled()) return;
    dropOnBoard(this.injector, data, this.board(), this.linkedItems);
  }

  protected readonly stitchCount$ = this.linkedInfoService
    .trackLinkedInfo(toObservable(this.stitchSummary), () => this.stitchSummary())
    .pipe(tap(items => { this.linkedItems = items; }), map((items) => items.length));

  protected openStitchItem(item: StitchItemSummaryDto): void {
    this.stitchItemOpener.openFullForm(item, this.injector, this.destroyRef);
  }

  /** Whether this card is in its list's selection — the one highlight a row has (list-selection §1): the
   * previewed board is the selected row, and a Shift/Cmd-click multi-selection highlights the same way. */
  protected readonly listSelected = toSignal(
    toObservable(this.position).pipe(
      switchMap((position) =>
        position === undefined || !this.selectableService ? of(false) : this.selectableService.getSelected(position),
      ),
    ),
    { initialValue: false },
  );

  /** Carries the whole multi-selection if this card is part of it, else just this board — via
   * `SelectableService.getDragData`, same as `BaseStitchComponent`'s own drag source, rather than
   * `listSelected()` gating a snapshot read of `getSelectedItemsSync()`: that boolean only flips when
   * THIS card's own membership in the selection changes, so selecting A then adding B to the selection
   * left A's `dragData` computed still holding its cached value from the moment `listSelected` last
   * changed (when A alone was selected) — `getSelectedItemsSync()` inside was a plain, non-reactive read
   * a `computed()` cannot see change on its own (SBUX-988 review). `getDragData` is a proper Observable
   * off the live selection itself, so it re-fires on every membership change, not just this card's own.
   *
   * No `initialValue` here: that option is evaluated eagerly, in this field initializer, before Angular
   * has assigned the required `board` input — reading `this.board()` there threw `NG0950` on every card,
   * crashing a non-empty list (SBUX-988 review, a regression introduced by the fix above). Left undefined
   * instead, `toSignal` reports `undefined` until the observable's own first emission; `dragData` below
   * falls back to reading `board()` itself, but only when it is actually evaluated for rendering — by then
   * the input is long since set, the same reason every other computed in this class safely reads it. */
  private readonly dragDataSource = toSignal(
    combineLatest([toObservable(this.position), toObservable(this.board)]).pipe(
      switchMap(([position, board]) =>
        position === undefined || !this.selectableService ? of([board]) : this.selectableService.getDragData(board, position),
      ),
    ),
  );

  /** The selection's rows carry their board as data (`[appSelectable]="item.board"`); anything else is
   * ignored rather than asserted into a board. */
  protected readonly dragData = computed<DragData>(() => {
    const boards = (this.dragDataSource() ?? []).filter(isBoard);
    return toBoardDragData(boards.length ? boards : [this.board()], this.injector.get(SpaceContextStore).contextId() ?? '');
  });

  /** Folders live entirely in memory already (`BoardsStore.folders()`), so this is a plain synchronous
   * lookup — unlike Notes/Files' own location badges, which resolve a remote notebook/folder name through
   * a batched summary cache. */
  protected readonly locationBadge = computed<{ name: string } | null>(() => {
    if (!this.showLocationBadge()) {
      return null;
    }
    const folderId = this.board().folderId;
    const folder = folderId ? this.store.folders().find((f) => f.id === folderId) : undefined;
    return folder ? { name: folder.title } : null;
  });

  /** The row miniature comes with the board from the server (`thumbnail`, written with the scene revision): no scene is read here. */
  protected readonly thumbnail = computed(() => thumbnailElements(this.board().thumbnail));

  /** Prototype `BoardLibraryPanel.tsx:84`: the description, else "N drawing elements" (a template is marked until the count is read). */
  protected readonly summary = computed(() => {
    const board = this.board();
    const description = htmlToPlainText(board.description);
    if (description) return description;
    // The element count rides with the list from the server; scenes are read only to draw miniatures.
    const count = board.elementCount;
    if (count !== undefined) return `${count} drawing elements`;
    return board.isTemplate ? 'Template' : 'Board';
  });

  protected archive(): void {
    this.store.archiveBoard(this.board().id);
  }

  protected trash(): void {
    this.actions.trash(this.board());
  }

  protected flag(event: MouseEvent): void {
    event.stopPropagation();
    this.store.toggleFlag(this.board().id);
  }

  protected pin(event: MouseEvent): void {
    event.stopPropagation();
    this.store.pinBoard(this.board().id);
  }
}
